import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useClienteAuth } from "@/contexts/ClienteAuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Monitor } from "lucide-react";
import { toast } from "sonner";
import { logoBlanco, logoColor } from "@/lib/logos";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Pantalla de entrada de la APP (iOS/Android/PWA instalada).
 *  - /movil           → login único (app nativa): detecta técnico o cliente.
 *  - /movil/tecnico   → App Técnicos (solo cuentas de técnico, tema oscuro).
 *  - /movil/cliente   → App Clientes (solo portal de clientes, tema claro).
 * Cada variante usa su propio manifest, así se instalan como apps separadas.
 * El administrador se gestiona solo desde la web.
 */
export default function Movil() {
  const { rol } = useParams();
  const esTecnico = rol === "tecnico";
  const esCliente = rol === "cliente";
  const claro = esCliente;

  const navigate = useNavigate();
  const { loginWithToken: clienteToken } = useClienteAuth();
  const tecnicoToken = (token) => {
    localStorage.setItem("odf_token", token);
    window.location.assign("/tecnico/dashboard"); // recarga: AuthProvider lee el token
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Manifest por app: instalar desde /movil/tecnico o /movil/cliente crea una
  // app separada, con su propio nombre y pantalla de inicio.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const prev = link.getAttribute("href");
    if (esTecnico) link.setAttribute("href", "/manifest-tecnicos.json");
    else if (esCliente) link.setAttribute("href", "/manifest-clientes.json");
    return () => link.setAttribute("href", prev);
  }, [esTecnico, esCliente]);

  // Sesiones existentes → entrar directo (respetando el rol de la app)
  useEffect(() => {
    if (!esCliente && localStorage.getItem("odf_token")) navigate("/tecnico/dashboard", { replace: true });
    else if (!esTecnico && localStorage.getItem("odf_cliente_token")) navigate("/cliente/dashboard", { replace: true });
  }, [navigate, esTecnico, esCliente]);

  const loginTecnico = async () => {
    const r = await axios.post(`${API_BASE}/api/auth/login`, { email, password });
    const { access_token: token, user } = r.data;
    if (user?.cargo === "admin") {
      setError("Las cuentas de administrador se gestionan desde la página web (odontofix.cl) en tu computador.");
      return;
    }
    tecnicoToken(token);
  };

  const loginCliente = async () => {
    const r = await axios.post(`${API_BASE}/api/portal/login`, { email, password });
    clienteToken(r.data.token);
    navigate("/cliente/dashboard", { replace: true });
  };

  const entrar = async () => {
    setError("");
    if (!email || !password) { setError("Ingresa tu correo y contraseña"); return; }
    setBusy(true);
    try {
      if (esCliente) {
        await loginCliente();
      } else if (esTecnico) {
        await loginTecnico();
      } else {
        // login único: primero técnico, luego cliente
        try {
          await loginTecnico();
          return;
        } catch (e) {
          if (e.response?.status !== 401 && e.response?.status !== 404) throw e;
        }
        await loginCliente();
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 403 && (d || "").includes("verificar")) {
        toast.info(d); navigate("/cliente");
      } else {
        setError(d || "Correo o contraseña incorrectos");
      }
    } finally { setBusy(false); }
  };

  const subtitulo = esTecnico
    ? "Acceso exclusivo para técnicos Odontofix."
    : esCliente
      ? "Tus equipos, documentos y mantenciones en un solo lugar."
      : "Una sola cuenta. Tu módulo te espera.";

  return (
    <div className={`min-h-screen ${claro ? "bg-white" : "bg-[#0B1020]"} flex items-center justify-center px-4 py-10`}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={claro ? logoColor : logoBlanco} alt="Odontofix" className="h-24 mx-auto" />
          {(esTecnico || esCliente) && (
            <h1 className={`mt-4 font-heading text-2xl font-bold ${claro ? "text-[#384091]" : "text-white"}`}>
              {esTecnico ? "App Técnicos" : "Portal de Clientes"}
            </h1>
          )}
          <p className={`text-sm mt-2 ${claro ? "text-gray-500" : "text-white/50"}`}>{subtitulo}</p>
        </div>

        <Card className={claro ? "border border-gray-200 shadow-lg" : "border-0 shadow-2xl"}>
          <CardContent className="p-6 space-y-4">
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                {error.includes("administrador") && <Monitor className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                <span>{error}</span>
              </div>
            )}
            <div>
              <Label className="text-sm">Correo</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-12 bg-white no-uppercase" placeholder="tucorreo@correo.cl"
                autoComplete="email" />
            </div>
            <div>
              <Label className="text-sm">Contraseña</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-12 bg-white no-uppercase" autoComplete="current-password"
                onKeyDown={(e) => e.key === "Enter" && entrar()} />
            </div>
            <Button className="w-full h-13 py-3.5 bg-[#0FB4B7] hover:bg-[#0D999C] text-white text-base font-semibold"
              onClick={entrar} disabled={busy}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Ingresar"}
            </Button>
            <div className="flex justify-between text-sm pt-1">
              {!esTecnico ? (
                <button className="text-[#0FB4B7] hover:underline" onClick={() => navigate("/cliente")}>
                  Crear cuenta clínica
                </button>
              ) : <span />}
              <button className="text-gray-400 hover:underline" onClick={() => navigate("/")}>
                Ir al sitio web
              </button>
            </div>
          </CardContent>
        </Card>

        {!esTecnico && !esCliente && (
          <p className="text-center text-[11px] text-white/30 mt-6">
            Técnicos y clínicas usan la misma puerta: tu cuenta define tu módulo.
          </p>
        )}
      </div>
    </div>
  );
}
